import { useStore, errText } from '@/store/useStore';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, ChevronsLeft, ChevronsRight, GripVertical, ChevronDown, Search,
  Save, Settings as SettingsIcon, X, Check, BadgeCheck,
} from 'lucide-react';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { addDays, formatHM, formatDateTimeRange, startOfDay, inBranchTz } from '@/utils/time';
import { cn } from '@/utils/cn';
import { mapUrl } from '@/utils/mapLink';
import type { Deal, Employee, Team } from '@/types';
import { isPrebooking, isPrebookingExpired, prebookingMsLeft, isFinanceVerified, dealEmoji } from '@/types';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useTzShiftMs } from '@/hooks/useTzShift';
import { hashHue, initialsOf } from '@/utils/colors';
import { usePerms } from '@/auth/usePerms';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Modal } from '@/components/ui/Modal';

type View = 'calendar' | 'day' | 'graph' | 'month' | 'resources';

// Месяцы в родительном падеже — для заголовка окна дня («14 августа»).
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/**
 * Виден ли ивент в календаре филиала `branchId`.
 * Событие показывается: в своём дом-филиале (branch_id) И, если задано «зеркало» города
 * (mirror_branch_id) и его не убрали (mirror_hidden), — ещё и в календаре этого города.
 */
function dealInBranch(d: Deal, branchId: string): boolean {
  if (branchId === 'all') return true;
  if (d.branch_id === branchId) return true;
  return d.mirror_branch_id === branchId && !d.mirror_hidden;
}

const MONTHS_FULL = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

/**
 * «Своя игра» текущего пользователя — та же логика, что isOwnGame в DealEditor:
 * он ответственный, или в составе смены (staff_assignments), или в участниках команды
 * игры (team.member_ids). uid — employee_id текущего пользователя.
 */
function isOwnDeal(deal: Deal, uid: string | null | undefined, team?: Team | null): boolean {
  if (!uid) return false;
  if (deal.responsible_id === uid) return true;
  if (Array.isArray(deal.staff_assignments) && deal.staff_assignments.some((s) => s.employee_id === uid)) return true;
  if (team?.member_ids?.includes(uid)) return true;
  return false;
}

/** Заметная зелёная точка «это ваша игра» — ставится слева от игры (у времени/номера команды). */
function OwnDot({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-block w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0', className)}
      title="Ваша игра"
      aria-label="Ваша игра"
    />
  );
}

/** Бирюзовая точка «заведена вручную в календаре, не из Битрикса» — у игр без bitrix_id. */
function ManualDot({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-block w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0', className)}
      title="Создана вручную в календаре, не из Битрикса"
      aria-label="Создана вручную в календаре, не из Битрикса"
    />
  );
}

/**
 * Лёгкий «тикающий» таймер: раз в `ms` возвращает свежий Date.now() и вызывает ререндер
 * (как часы в TopNav). Нужен, чтобы обратный отсчёт предброни шёл, а истёкшие предброни
 * сами уходили из календаря/ленты, не занимая слот.
 */
function useNowTick(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(id); }, [ms]);
  return now;
}

/** Обратный отсчёт предброни: «⏳ 1:23» (ч:мм) или «⏳ 12м». Обновляется сам раз в 30с. */
function PrebookingBadge({ deal, className }: { deal: Deal; className?: string }) {
  const now = useNowTick(30_000);
  if (!isPrebooking(deal) || deal.prebooking_expires_at == null) return null;
  const leftMs = prebookingMsLeft(deal, now);
  const mins = Math.ceil(leftMs / 60_000);
  const label = leftMs <= 0 ? 'истекла' : mins >= 60 ? `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}` : `${mins}м`;
  const soon = leftMs > 0 && leftMs <= 15 * 60_000; // < 15 мин — тревожный оттенок
  return (
    <span
      title={leftMs <= 0 ? 'Предбронь истекла' : `Предбронь: осталось ${label} до истечения`}
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1 text-[9px] font-semibold tabular-nums flex-shrink-0',
        leftMs <= 0 ? 'bg-status-refused/20 text-status-refused' : soon ? 'bg-status-refused/15 text-status-refused' : 'bg-amber-100 text-amber-700',
        className,
      )}
    >
      ⏳{label}
    </span>
  );
}

/**
 * Неброская, но заметная пометка «ивент» — отличает событие (deal.kind === 'event')
 * от обычной игры. compact=true (тесные чипы месяца/ресурсов) — только значок 🎪;
 * иначе — 🎪 + подпись «ивент». На обычных играх ничего не рисует.
 */
function EventBadge({ deal, compact, className }: { deal: Deal; compact?: boolean; className?: string }) {
  if (deal.kind !== 'event') return null;
  return (
    <span
      title="Ивент"
      aria-label="Ивент"
      className={cn(
        'inline-flex items-center gap-0.5 flex-shrink-0 rounded leading-none font-semibold bg-fuchsia-100 text-fuchsia-700',
        compact ? 'px-0.5 text-[9px]' : 'px-1 text-[9px]',
        className,
      )}
    >
      🎪{!compact && <span>ивент</span>}
    </span>
  );
}

export function CalendarPage() {
  const {
    branches, teams, employees, deals, setModal, settings, filterBranchId, newDeal,
    markerFilter, setMarkerFilter,
  } = useStore();
  const perms = usePerms();
  const canCreate = perms.deals.create;
  const canEdit = perms.deals.edit; // перемещать/двигать игру — только с правом правки (не ОКК/финансист)
  const employeeById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const tzShiftMs = useTzShiftMs();
  const nowTick = useNowTick(); // тикает раз в 30с — двигает отсчёт предброни и убирает истёкшие
  void useHotkeys;

  const [view, setView] = useState<View>('calendar');
  // Единая «опорная» дата для всех видов — НЕ сбрасывается при переключении День/График/Ресурсы.
  const [anchorDay, setAnchorDay] = useState(() => startOfDay(Date.now()));
  const currentMonth = useMemo(() => {
    const d = new Date(anchorDay); return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  }, [anchorDay]);
  const shiftAnchorMonth = (dir: number) => setAnchorDay((prev) => {
    const d = new Date(prev);
    const first = new Date(d.getFullYear(), d.getMonth() + dir, 1);
    const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    first.setDate(Math.min(d.getDate(), lastDay));
    return startOfDay(first.getTime());
  });
  // Форс-вид при заходе из карточки сделки («Открыть в календаре (Гант)» → сразу «Ресурсы»
  // на день события). Читаем один раз при монтировании и сразу очищаем флаги, чтобы обычный
  // заход на календарь не форсировал вид.
  useEffect(() => {
    const { pendingCalView, pendingCalDay, setPendingCal } = useStore.getState();
    if (pendingCalView) {
      setView(pendingCalView as View);
      if (pendingCalDay != null) setAnchorDay(startOfDay(pendingCalDay));
      setPendingCal(null, null);
    }
  }, []); // eslint-disable-line

  // Клик по числу дня в месячной сетке открывает большое окно-список игр этого дня.
  // Храним timestamp начала дня (ключ dealsByDay) либо null (окно закрыто).
  const [dayListDay, setDayListDay] = useState<number | null>(null);

  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  // На телефоне боковая панель по умолчанию свёрнута (иначе съедает весь экран);
  // открывается поверх сетки (оверлеем), а не раздвигает её.
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);
  const [sidebarTab, setSidebarTab] = useState<'teams' | 'markers'>('teams');
  const [enabledTeamIds, setEnabledTeamIds] = useState<Set<string>>(() => new Set(teams.map((t) => t.id)));
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(() => new Set());
  // Существующие команды — чтобы отличить «снятую галочкой» команду от «мёртвой»
  // (удалённой): у сделки в data.team_id могла остаться ссылка на удалённую команду.
  const existingTeamIds = useMemo(() => new Set(teams.map((t) => t.id)), [teams]);

  // Подключаем все новые команды по умолчанию
  useEffect(() => {
    setEnabledTeamIds((prev) => {
      const s = new Set(prev);
      teams.forEach((t) => s.add(t.id));
      return s;
    });
  }, [teams.length]); // eslint-disable-line

  // Хоткеи навигации
  useEffect(() => {
    const onNav = (e: Event) => {
      const dir = (e as CustomEvent<{ dir: number }>).detail?.dir ?? 0;
      shiftAnchorMonth(dir);
    };
    const onToday = () => setAnchorDay(startOfDay(Date.now()));
    window.addEventListener('cal:nav', onNav);
    window.addEventListener('cal:today', onToday);
    return () => {
      window.removeEventListener('cal:nav', onNav);
      window.removeEventListener('cal:today', onToday);
    };
  }, [currentMonth]);

  // Дни месяца + хвосты
  const monthCells = useMemo(() => {
    const first = new Date(currentMonth);
    const firstDow = (first.getDay() + 6) % 7;
    const start = new Date(first); start.setDate(first.getDate() - firstDow);
    start.setHours(0, 0, 0, 0);
    const cells: number[] = [];
    for (let i = 0; i < 42; i++) cells.push(addDays(start.getTime(), i));
    return cells;
  }, [currentMonth]);

  const currentMonthIdx = new Date(currentMonth).getMonth();
  const currentYear = new Date(currentMonth).getFullYear();

  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const orderedTeams = useMemo(() => {
    const own = teams.filter((t) => filterBranchId === 'all' || t.branch_id === filterBranchId);
    // На игру филиала может быть назначена команда ДРУГОГО филиала: у ивента своя площадка,
    // а бригада приезжает из города (карточка разрешает выбрать команду города-зеркала).
    // Без этого такая игра пропадала бы совсем: в «Ресурсах» нет её дорожки, а в «без
    // команды» она не попадает — команда-то указана. Поэтому дописываем команды, реально
    // занятые на играх этого филиала, даже если сами они числятся за другим городом.
    const ownIds = new Set(own.map((t) => t.id));
    // Смотрим ТОЛЬКО игры видимого периода (сетка месяца = 42 дня). Если брать всю базу,
    // команда, разово приехавшая на ивент, навсегда получит дорожку в «Ресурсах» и галочку
    // в фильтре — в том числе в дни, когда она не работает. А на боевом уже есть полсотни
    // старых игр с командой чужого города, и они бы засорили панель во всех филиалах.
    const periodFrom = monthCells[0];
    const periodTo = monthCells[monthCells.length - 1] + 24 * 3600_000;
    const usedIds = new Set(
      deals.filter((d) => {
        if (!d.team_id || !dealInBranch(d, filterBranchId)) return false;
        const at = d.event.start_at ?? d.event.event_date;
        return at != null && at >= periodFrom && at < periodTo;
      }).map((d) => d.team_id) as string[],
    );
    // Гостевой команде дописываем город прямо в название: бригады в филиалах названы
    // одинаково («Команда 1»), и без города своя и приезжая в списке неразличимы.
    const guests = teams.filter((t) => usedIds.has(t.id) && !ownIds.has(t.id))
      .map((t) => {
        const city = branches.find((b) => b.id === t.branch_id)?.name;
        return city ? { ...t, name: `${t.name} · ${city}` } : t;
      });
    return [...own, ...guests]
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.name.localeCompare(b.name));
  }, [teams, filterBranchId, deals, branches, monthCells]);
  const teamNum = useMemo(() => new Map(orderedTeams.map((t, i) => [t.id, i + 1])), [orderedTeams]);
  // В режиме «Все филиалы» команды разных городов часто называются одинаково («Команда 1»,
  // «Команда 2» — нумерация внутри каждого города своя), в общем плоском списке это выглядит
  // как дубли. Группируем по филиалу, чтобы сразу было видно, какая команда из какого города.
  // Нумерация (indexNum) — та же сквозная, что в orderedTeams, чтобы совпадать с цифрой на
  // карточках игр в календаре.
  const teamGroups = useMemo(() => {
    if (filterBranchId !== 'all') return null;
    const groups = new Map<string, { branchName: string; branchColor: string | null; items: { team: Team; idx: number }[] }>();
    orderedTeams.forEach((t, i) => {
      const branch = branches.find((b) => b.id === t.branch_id);
      const key = t.branch_id ?? '—';
      if (!groups.has(key)) {
        groups.set(key, { branchName: branch?.name ?? 'Без филиала', branchColor: branch?.color ?? null, items: [] });
      }
      groups.get(key)!.items.push({ team: t, idx: i + 1 });
    });
    return [...groups.values()].sort((a, b) => a.branchName.localeCompare(b.branchName));
  }, [orderedTeams, filterBranchId, branches]);

  const visibleDeals = useMemo(() => {
    const localOff = -new Date().getTimezoneOffset();
    return deals.filter((d) => {
      // Отказы и ИСТЁКШИЕ предброни не показываем — слот свободен (предбронь протухла).
      if (d.status === 'refused' || isPrebookingExpired(d, nowTick)) return false;
      if (!dealInBranch(d, filterBranchId)) return false;
      // Прячем по фильтру команд ТОЛЬКО реально существующую команду, снятую галочкой.
      // Если team_id указывает на удалённую команду (висячая ссылка) — не прячем, иначе
      // игра пропадает из календаря навсегда (в дашборде при этом остаётся).
      if (d.team_id && existingTeamIds.has(d.team_id) && !enabledTeamIds.has(d.team_id)) return false;
      // Маркеры (вкладка «Маркеры») больше НЕ прячут карточки — они помечают значком
      // (см. dealMarker / MarkerBadge). Поэтому здесь фильтра по маркеру нет.
      const ts = d.event.start_at ?? d.event.event_date;
      if (!ts) return false;
      const branch = branches.find((b) => b.id === d.branch_id);
      const base = settings.use_branch_tz_in_dashboards && branch
        ? inBranchTz(ts, branch.tz_offset_minutes, localOff)
        : ts;
      const adj = base + tzShiftMs;
      return adj >= monthCells[0] && adj < monthCells[41] + 24 * 3600_000;
    });
  }, [deals, monthCells, enabledTeamIds, existingTeamIds, filterBranchId, branches, settings.use_branch_tz_in_dashboards, tzShiftMs, nowTick]);

  const dealsByDay = useMemo(() => {
    const m = new Map<number, Deal[]>();
    const localOff = -new Date().getTimezoneOffset();
    for (const d of visibleDeals) {
      const ts = d.event.start_at ?? d.event.event_date!;
      const branch = branches.find((b) => b.id === d.branch_id);
      const base = settings.use_branch_tz_in_dashboards && branch
        ? inBranchTz(ts, branch.tz_offset_minutes, localOff)
        : ts;
      const dayKey = startOfDay(base + tzShiftMs);
      const arr = m.get(dayKey) ?? [];
      arr.push(d);
      m.set(dayKey, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0));
    return m;
  }, [visibleDeals, branches, settings.use_branch_tz_in_dashboards, tzShiftMs]);

  // Соседи для листания карточки в месячной сетке: все события месяца в экранном порядке
  // (по дням слева направо / сверху вниз, внутри дня — по времени начала).
  const monthNavIds = useMemo(
    () => monthCells.flatMap((day) => (dealsByDay.get(day) ?? []).map((d) => d.id)),
    [monthCells, dealsByDay],
  );

  const today0 = startOfDay(Date.now());

  // ── DnD ──
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const [activeDrag, setActiveDrag] = useState<{ type: 'team' | 'deal' | 'employee'; id: string; fromTeamId?: string } | null>(null);

  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { type?: string; teamId?: string; dealId?: string; employeeId?: string; fromTeamId?: string } | undefined;
    if (data?.type === 'team') setActiveDrag({ type: 'team', id: data.teamId! });
    else if (data?.type === 'deal') setActiveDrag({ type: 'deal', id: data.dealId! });
    else if (data?.type === 'employee') setActiveDrag({ type: 'employee', id: data.employeeId!, fromTeamId: data.fromTeamId });
  };

  const onDragEnd = async (e: DragEndEvent) => {
    const drag = activeDrag;
    setActiveDrag(null);
    const data = e.active.data.current as { type?: string; teamId?: string; dealId?: string; employeeId?: string; fromTeamId?: string } | undefined;
    const over = e.over?.data.current as { type?: string; day?: number; teamId?: string | null } | undefined;

    // Перетаскивание сделки внутри вкладки «Ресурсы» (Гант по командам):
    //  · по вертикали (смена дорожки) → смена КОМАНДЫ (team_id);
    //  · по горизонтали (сдвиг по временной оси) → смена ВРЕМЕНИ/ДАТЫ игры (start_at/end_at).
    // Управляющий и другие роли с правом правки двигают игру и по времени, и по команде
    // (решение созвона 03–04.07; раньше время было заблокировано «т.к. из Битрикса»).
    if (data?.type === 'resourceDeal') {
      if (!over || over.type !== 'resourceRow') return;
      const d = deals.find((x) => x.id === data.dealId);
      if (!d) return;
      if (!canEdit) { useStore.getState().toast({ kind: 'warn', text: 'Эта роль не может перемещать игру.' }); return; }
      const newTeamId = over.teamId ?? null;
      const teamChanged = newTeamId !== (d.team_id ?? null);

      // Горизонтальный сдвиг курсора → сдвиг времени: ширина часа на ленте фиксирована (RES_HOUR_W).
      const oldStart = d.event.start_at;
      const oldEnd = d.event.end_at ?? (oldStart != null ? oldStart + 2 * 3600_000 : undefined);
      const durMs = oldStart != null && oldEnd != null ? oldEnd - oldStart : 2 * 3600_000;
      let newStart = oldStart;
      let newEnd = oldEnd;
      // База для отсчёта времени: если у события времени НЕТ, на ленте оно рисуется по умолчанию
      // в 18:00 — от неё и считаем, чтобы перетаскивание ЗАДАВАЛО время (а не выходило вхолостую).
      const baseStart = oldStart ?? ((over.day ?? startOfDay(Date.now())) + 18 * 3600_000);
      const movedX = Math.abs(e.delta.x) >= 4; // был реальный горизонтальный сдвиг (не клик)
      if (oldStart != null || movedX) {
        const SNAP = 15 * 60_000; // округляем результат к 15-минутной сетке
        const rawStart = baseStart + (e.delta.x / RES_HOUR_W) * 3600_000;
        // Снап в отображаемом поясе (tzShiftMs), затем обратно в UTC-мс хранения.
        newStart = Math.round((rawStart + tzShiftMs) / SNAP) * SNAP - tzShiftMs;
        newEnd = newStart + durMs; // длительность сохраняем
      }
      const timeChanged = newStart !== oldStart;
      if (!teamChanged && !timeChanged) return; // ничего не изменилось

      // Проверка наложения/перерыва идёт уже по НОВОМУ времени и НОВОЙ команде.
      const curStart = newStart;
      const curEnd = newEnd;
      // Дата события сдвигается на столько же суток, на сколько ушёл старт (в норме 0 — лента однодневная).
      const newEventDate = (oldStart != null && d.event.event_date != null && newStart != null)
        ? d.event.event_date + (startOfDay(newStart) - startOfDay(oldStart))
        : d.event.event_date;
      const applyMove = async () => {
        try {
          // Перенос мышкой раньше менял время «немо» — в историю ничего не писалось.
          // Если время реально сдвинулось, фиксируем «было→стало», как при правке в карточке.
          const author = useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined;
          const nextHistory = timeChanged
            ? [...(d.history ?? []), { ts: Date.now(), user: author, action: 'Изменена информация об игре',
                changes: [{ label: 'Время игры', from: formatDateTimeRange(oldStart, oldEnd, tzShiftMs), to: formatDateTimeRange(newStart, newEnd, tzShiftMs) }] }]
            : d.history;
          await useStore.getState().saveDeal({
            ...d,
            team_id: newTeamId,
            history: nextHistory,
            event: {
              ...d.event,
              event_date: newEventDate,
              start_at: newStart,
              end_at: newEnd,
              // Закрепляем время за ERP ТОЛЬКО если его реально изменили. Перенос игры
              // на другую команду (время то же) время не трогает — иначе игра «замерзала»
              // и правка времени в Битриксе до неё больше не доезжала.
              ...(timeChanged ? { time_overridden: true } : {}),
            },
          });
        } catch (err) {
          useStore.getState().toast({ kind: 'warn', text: errText(err) });
        }
      };
      const breakMs = (settings.resource_break_minutes ?? 60) * 60_000;
      const overlap = curStart != null && curEnd != null && deals.some((o) => {
        // Истёкшая предбронь слот НЕ занимает — как и отказ.
        if (o.id === d.id || o.status === 'refused' || isPrebookingExpired(o)) return false;
        if ((o.team_id ?? null) !== newTeamId) return false;
        const os = o.event.start_at;
        const oe = o.event.end_at ?? (os != null ? os + 2 * 3600_000 : null);
        if (os == null || oe == null) return false;
        // конфликт, если разрыв между событиями меньше перерыва (с любой стороны)
        return curStart < oe + breakMs && os < curEnd + breakMs;
      });
      if (overlap) {
        setModal({
          kind: 'confirm',
          title: 'Слишком близко к другому событию',
          message: 'Событие попадает на другую игру этой команды или в её перерыв. Точно перенести на это время?',
          onConfirm: () => { void applyMove(); },
        });
        return;
      }
      await applyMove();
      return;
    }

    if (!over || over.type !== 'cell' || over.day == null) return;

    if (data?.type === 'team') {
      if (!canCreate) return;
      // Создать новую сделку с этой командой на этот день в 18:00–20:00
      const day = over.day;
      const branchId = filterBranchId !== 'all' ? filterBranchId : branches[0]?.id ?? null;
      const newD = await newDeal('b2c', {
        team_id: data.teamId,
        branch_id: branchId,
        event: {
          event_date: day,
          start_at: day + 18 * 3600_000,
          end_at: day + 20 * 3600_000,
          emoji: '📋',
        },
      });
      setModal({ kind: 'deal', id: newD.id });
    } else if (data?.type === 'employee') {
      if (!canCreate) return;
      // Создать сделку с этим сотрудником (responsible + staff_assignments)
      const day = over.day;
      const emp = employeeById.get(data.employeeId!);
      const branchId = filterBranchId !== 'all'
        ? filterBranchId
        : emp?.branch_id ?? branches[0]?.id ?? null;
      const newD = await newDeal('b2c', {
        team_id: data.fromTeamId ?? drag?.fromTeamId,
        branch_id: branchId,
        responsible_id: data.employeeId,
        staff_assignments: [{ employee_id: data.employeeId!, role: emp?.role ?? 'host', employee_name: emp?.full_name }],
        event: {
          event_date: day,
          start_at: day + 18 * 3600_000,
          end_at: day + 20 * 3600_000,
          emoji: '📋',
        },
      });
      setModal({ kind: 'deal', id: newD.id });
    } else if (data?.type === 'deal') {
      // Перенос существующей сделки
      const d = deals.find((x) => x.id === data.dealId);
      if (!d) return;
      if (!canEdit) { useStore.getState().toast({ kind: 'warn', text: 'Эта роль не может перемещать игру.' }); return; }
      const oldDay = d.event.event_date != null ? startOfDay(d.event.event_date) : null;
      const newDay = over.day;
      const delta = oldDay != null ? newDay - oldDay : 0;
      // Новое время считаем один раз — используем и в сохранении, и в записи истории.
      const newStart = d.event.start_at != null && oldDay != null ? d.event.start_at + delta : newDay + 18 * 3600_000;
      const newEnd = d.event.end_at != null && oldDay != null ? d.event.end_at + delta : newDay + 20 * 3600_000;
      // Дата реально сдвинулась (или времени не было вовсе) → это изменение времени.
      // Бросок на тот же день ничего не меняет.
      const timeChanged = delta !== 0 || oldDay == null;
      try {
        // Перенос мышкой раньше не оставлял следа в истории — теперь фиксируем «было→стало».
        const author = useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined;
        const nextHistory = timeChanged
          ? [...(d.history ?? []), { ts: Date.now(), user: author, action: 'Изменена информация об игре',
              changes: [{ label: 'Время игры', from: formatDateTimeRange(d.event.start_at, d.event.end_at, tzShiftMs), to: formatDateTimeRange(newStart, newEnd, tzShiftMs) }] }]
          : d.history;
        await useStore.getState().saveDeal({
          ...d,
          history: nextHistory,
          event: {
            ...d.event,
            event_date: newDay,
            start_at: newStart,
            end_at: newEnd,
            ...(timeChanged ? { time_overridden: true } : {}),
          },
        });
      } catch (err) {
        useStore.getState().toast({ kind: 'warn', text: errText(err) });
      }
    }
  };

  const activeTeam = activeDrag?.type === 'team' ? teamById.get(activeDrag.id) : null;
  const activeDealDrag = activeDrag?.type === 'deal' ? deals.find((d) => d.id === activeDrag.id) : null;
  const activeEmployee = activeDrag?.type === 'employee' ? employeeById.get(activeDrag.id) : null;
  const activeColor = activeTeam?.color
    ?? (activeDrag?.fromTeamId ? teamById.get(activeDrag.fromTeamId)?.color : undefined);

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="h-full flex relative">
        {/* Затемнение под оверлеем боковой панели (только телефон) */}
        {isMobile && sidebarOpen && (
          <div className="absolute inset-0 z-30 bg-black/30" onClick={() => setSidebarOpen(false)} />
        )}
        {/* Sidebar */}
        <aside
          className={cn(
            'border-r border-line bg-surface-1 flex flex-col flex-shrink-0 transition-all duration-200',
            isMobile && 'absolute inset-y-0 left-0 z-40 shadow-2xl',
            sidebarOpen ? 'w-[240px]' : 'w-0 overflow-hidden',
          )}
        >
          <div className="h-12 px-3 flex items-center justify-between">
            <button onClick={() => setSidebarOpen(false)} className="text-xs text-ink-3 hover:text-ink-1 flex items-center gap-1">
              <ChevronsLeft className="w-3.5 h-3.5" /> Свернуть
            </button>
          </div>
          {canCreate && (
            <button
              className="mx-3 mb-2 btn-primary uppercase text-xs tracking-wider"
              onClick={() => setModal({ kind: 'deal', id: null })}
            >
              <Plus className="w-4 h-4" /> Добавить
            </button>
          )}
          <div className="px-3 mb-2">
            <div className="flex gap-3 text-xs border-b border-line">
              <button
                className={cn('px-1 pb-2 border-b-2', sidebarTab === 'teams' ? 'border-accent text-accent font-semibold' : 'border-transparent text-ink-3 hover:text-ink-1')}
                onClick={() => setSidebarTab('teams')}
              >Команды</button>
              <button
                className={cn('px-1 pb-2 border-b-2', sidebarTab === 'markers' ? 'border-accent text-accent font-semibold' : 'border-transparent text-ink-3 hover:text-ink-1')}
                onClick={() => setSidebarTab('markers')}
              >Маркеры</button>
            </div>
          </div>

          {sidebarTab === 'markers' ? (
            <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-1">
              <div className="text-[10px] text-ink-3 italic mb-1.5">Помечать значком сделки с маркером (карточки не прячутся):</div>
              <MarkerRadio
                label="Без маркеров"
                checked={markerFilter === 'none'}
                onChange={() => setMarkerFilter('none')}
              />
              <MarkerRadio
                label="Подтверждено"
                icon={<Check className="w-4 h-4 text-accent" />}
                checked={markerFilter === 'confirmed'}
                onChange={() => setMarkerFilter('confirmed')}
                count={visibleDeals.filter((d) => d.confirmed).length}
              />
              <MarkerRadio
                label="Финансово проверено"
                icon={<BadgeCheck className="w-4 h-4 text-status-paid" />}
                checked={markerFilter === 'finance'}
                onChange={() => setMarkerFilter('finance')}
                count={visibleDeals.filter((d) => isFinanceVerified(d)).length}
              />
            </div>
          ) : (
          <>
          <div className="px-3 mb-2 text-[10px] text-ink-3 italic flex items-start gap-1.5">
            <span className="text-accent">💡</span>
            <span>Тащи команду или сотрудника (разверни ▾) на день — создаст сделку.</span>
          </div>

          <div className="flex-1 overflow-y-auto px-1 pb-2 space-y-0.5">
            <TeamCheckbox
              checked={enabledTeamIds.size === teams.length}
              label="Все"
              color="#a09ac0"
              onChange={() => {
                if (enabledTeamIds.size === teams.length) setEnabledTeamIds(new Set());
                else setEnabledTeamIds(new Set(teams.map((t) => t.id)));
              }}
            />
            <div className="h-px bg-line/60 my-1" />
            {(() => {
              const row = (t: Team, idx: number) => {
                const teamMembers = t.member_ids
                  .map((mid) => employeeById.get(mid))
                  .filter((e): e is Employee => !!e);
                return (
                  <DraggableTeamRow
                    key={t.id}
                    team={t}
                    indexNum={idx}
                    members={teamMembers}
                    expanded={expandedTeamIds.has(t.id)}
                    onToggleExpand={() => setExpandedTeamIds((prev) => {
                      const s = new Set(prev);
                      if (s.has(t.id)) s.delete(t.id); else s.add(t.id);
                      return s;
                    })}
                    checked={enabledTeamIds.has(t.id)}
                    onToggle={() => setEnabledTeamIds((prev) => {
                      const s = new Set(prev);
                      if (s.has(t.id)) s.delete(t.id); else s.add(t.id);
                      return s;
                    })}
                    onEdit={() => setModal({ kind: 'team', id: t.id })}
                  />
                );
              };
              if (teamGroups) {
                return teamGroups.map((g) => (
                  <div key={g.branchName}>
                    <div className="flex items-center gap-1.5 px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                      {g.branchColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: g.branchColor }} />}
                      {g.branchName}
                    </div>
                    {g.items.map(({ team, idx }) => row(team, idx))}
                  </div>
                ));
              }
              return orderedTeams.map((t, i) => row(t, i + 1));
            })()}
            {teams.length === 0 && (
              <div className="text-xs text-ink-3 italic text-center py-4 px-2">
                Нет команд. Создай в разделе «Персонал».
              </div>
            )}
          </div>
          </>
          )}
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Toolbar */}
          <div className="min-h-0 sm:min-h-12 px-1 sm:px-4 py-1 sm:py-0 flex items-center flex-nowrap sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible border-b border-line bg-surface-1 flex-shrink-0">
            {!sidebarOpen && (
              <button onClick={() => setSidebarOpen(true)} className="icon-btn !w-8 !h-8" title="Развернуть боковую панель">
                <ChevronsRight className="w-4 h-4" />
              </button>
            )}
            {(view === 'calendar' || view === 'month') && (<>
            <button className="icon-btn !w-8 !h-8" onClick={() => shiftAnchorMonth(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              className="px-3 h-8 text-xs font-medium rounded-full bg-surface-2 hover:bg-surface-3 transition-colors"
              onClick={() => setAnchorDay(startOfDay(Date.now()))}
            >
              Сегодня
            </button>
            <button className="icon-btn !w-8 !h-8" onClick={() => shiftAnchorMonth(1)}>
              <ChevronRight className="w-4 h-4" />
            </button>
            <h2 className="text-sm font-semibold text-ink-1 ml-2 capitalize">
              {MONTHS_FULL[currentMonthIdx]} {currentYear} г.
            </h2>
            </>)}

            {(view === 'day' || view === 'resources') && (<>
            <button className="icon-btn !w-8 !h-8 shrink-0" onClick={() => setAnchorDay(addDays(anchorDay, -1))} title="Предыдущий день"><ChevronLeft className="w-4 h-4" /></button>
            <button className="px-3 h-8 text-xs font-medium rounded-full bg-surface-2 hover:bg-surface-3 transition-colors shrink-0" onClick={() => setAnchorDay(startOfDay(Date.now()))}>Сегодня</button>
            <button className="icon-btn !w-8 !h-8 shrink-0" onClick={() => setAnchorDay(addDays(anchorDay, 1))} title="Следующий день"><ChevronRight className="w-4 h-4" /></button>
            <h2 className="text-sm font-semibold text-ink-1 ml-1 shrink-0 whitespace-nowrap">{new Date(anchorDay).getDate()} {MONTHS_FULL[new Date(anchorDay).getMonth()]}</h2>
            </>)}

            <div className="hidden sm:block flex-1" />

            <div className="flex items-center gap-0.5 p-0.5 rounded-lg border border-line bg-surface-1 text-[10px] uppercase font-bold tracking-wider shrink-0">
              {(['calendar', 'day', 'graph', 'month', 'resources'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={cn(
                    'px-2.5 h-7 rounded-md transition-colors shrink-0 whitespace-nowrap',
                    view === v
                      ? 'bg-accent text-white shadow-soft'
                      : 'text-accent/70 hover:text-accent hover:bg-accent-soft/40',
                  )}
                >
                  {v === 'calendar' ? 'Календарь' : v === 'day' ? 'День' : v === 'graph' ? 'График' : v === 'month' ? 'Месяц' : 'Ресурсы'}
                </button>
              ))}
            </div>
          </div>

          {/* Body — switches by view */}
          <div className="flex-1 overflow-auto">
            {view === 'calendar' && (
              <div className="min-w-[640px] lg:min-w-0 h-full flex flex-col">
                <div className="grid grid-cols-7 border-b border-line bg-surface-1 sticky top-0 z-10 flex-shrink-0">
                  {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => (
                    <div key={d} className="h-8 px-2 flex items-center text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
                      {d}
                    </div>
                  ))}
                </div>
                {/* flex-1 + auto-rows-fr — недели тянутся на всю оставшуюся высоту, а не
                    занимают место только под свой контент (иначе снизу пустая полоса). */}
                <div className="grid grid-cols-7 auto-rows-fr flex-1">
                  {monthCells.map((day, i) => {
                    const d = new Date(day);
                    const dayDeals = dealsByDay.get(day) ?? [];
                    const isOtherMonth = d.getMonth() !== currentMonthIdx;
                    const isToday = day === today0;
                    const isWeekend = i % 7 >= 5;
                    return (
                      <DayCell
                        key={day}
                        day={day}
                        isOtherMonth={isOtherMonth}
                        isToday={isToday}
                        isWeekend={isWeekend}
                        deals={dayDeals}
                        teamById={teamById}
                        activeTeamColor={activeColor}
                        onDealClick={(deal) => setModal({ kind: 'deal', id: deal.id }, monthNavIds)}
                        onDayClick={() => setDayListDay(day)}
                        onMore={(deals) => setModal({
                          kind: 'confirm',
                          title: `${d.getDate()} ${MONTHS_FULL[d.getMonth()]}: все события (${deals.length})`,
                          message: deals.map((dd) => `• ${dd.number} · ${dd.contact_name}`).join('\n'),
                          onConfirm: () => {},
                        })}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {view === 'day' && (
              <DayView
                day={anchorDay}
                setDay={setAnchorDay}
                deals={deals}
                teamById={teamById}
                teamNum={teamNum}
                filterBranchId={filterBranchId}
                onDealClick={(d, navIds) => setModal({ kind: 'deal', id: d.id }, navIds)}
              />
            )}
            {view === 'month' && (
              <MonthView
                month={currentMonth}
                deals={deals}
                teamById={teamById}
                teamNum={teamNum}
                filterBranchId={filterBranchId}
                onDealClick={(d, navIds) => setModal({ kind: 'deal', id: d.id }, navIds)}
              />
            )}
            {view === 'graph' && (
              <GraphView
                day={anchorDay}
                setDay={setAnchorDay}
                deals={deals}
                employees={employees}
                teamById={teamById}
                teamNum={teamNum}
                filterBranchId={filterBranchId}
                onDealClick={(d, navIds) => setModal({ kind: 'deal', id: d.id }, navIds)}
              />
            )}
            {view === 'resources' && (
              <ResourcesView
                day={anchorDay}
                setDay={setAnchorDay}
                teams={orderedTeams}
                deals={deals}
                enabledTeamIds={enabledTeamIds}
                filterBranchId={filterBranchId}
                breakMinutes={settings.resource_break_minutes ?? 60}
                onDealClick={(d, navIds) => setModal({ kind: 'deal', id: d.id }, navIds)}
              />
            )}
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay dropAnimation={null} zIndex={9999}>
          {activeTeam && (
            <div
              className="px-3 py-2 rounded-lg shadow-pop flex items-center gap-2 border-2 border-white/40 text-white font-semibold text-sm"
              style={{ backgroundColor: activeTeam.color, maxWidth: 240 }}
            >
              <span className="w-2 h-2 rounded-full bg-white/90" />
              <span className="truncate">{activeTeam.name}</span>
            </div>
          )}
          {activeDealDrag && (
            <div className="px-2 py-1 rounded-md shadow-pop bg-accent text-white text-xs font-semibold border-2 border-white/40"
              style={{ maxWidth: 220 }}>
              {activeDealDrag.contact_name || activeDealDrag.number}
            </div>
          )}
          {activeEmployee && (
            <div className="px-2 py-1.5 rounded-lg shadow-pop bg-surface-1 border-2 border-accent flex items-center gap-2 text-sm font-semibold text-ink-1"
              style={{ maxWidth: 240 }}>
              <span className="w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ backgroundColor: `hsl(${hashHue(activeEmployee.full_name)}, 55%, 48%)` }}>
                {initialsOf(activeEmployee.full_name)}
              </span>
              <span className="truncate">{activeEmployee.full_name}</span>
            </div>
          )}
        </DragOverlay>

        {/* Большое окно по центру со списком игр выбранного дня (клик по числу в сетке).
            Клик по игре открывает её карточку и закрывает это окно. */}
        {dayListDay != null && (
          <DayListModal
            day={dayListDay}
            deals={dealsByDay.get(dayListDay) ?? []}
            teamById={teamById}
            teamNum={teamNum}
            onClose={() => setDayListDay(null)}
            onDealClick={(deal, navIds) => { setModal({ kind: 'deal', id: deal.id }, navIds); setDayListDay(null); }}
          />
        )}
      </div>
    </DndContext>
  );
}

// ── Окно-список игр одного дня (модалка по центру) ──────
// Открывается кликом по числу дня в месячной сетке. Крупное центрированное окно
// (переиспользует общий <Modal>: затемнение, крестик, Esc, клик по фону, скролл
// внутри, адаптив под телефон). Список строит той же строкой, что вид «День»
// (компонент DayDealRow). deals приходят уже отсортированными по времени (dealsByDay).
function DayListModal({ day, deals, teamById, teamNum, onClose, onDealClick }: {
  day: number; deals: Deal[]; teamById: Map<string, Team>; teamNum: Map<string, number>;
  onClose: () => void;
  onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const shiftMs = useTzShiftMs();
  const uid = useStore((s) => s.currentUser?.employee_id) ?? null;
  const fmt = (ts: number) => formatHM(ts + shiftMs);
  const d = new Date(day);
  const navIds = useMemo(() => deals.map((x) => x.id), [deals]);
  const title = `${d.getDate()} ${MONTHS_GEN[d.getMonth()]} — игры (${deals.length})`;

  return (
    <Modal open onClose={onClose} title={title} size="lg">
      {deals.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-3">На этот день игр нет.</div>
      ) : (
        <div className="-mx-5 -my-5">
          {deals.map((deal, i) => (
            <DayDealRow
              key={deal.id}
              deal={deal}
              index={i}
              navIds={navIds}
              teamById={teamById}
              teamNum={teamNum}
              uid={uid}
              fmt={fmt}
              onDealClick={onDealClick}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Day Cell (droppable) ────────────────────────────────

function DayCell({
  day, isOtherMonth, isToday, isWeekend, compact, deals, teamById, activeTeamColor,
  onDealClick, onMore, onDayClick,
}: {
  day: number; isOtherMonth: boolean; isToday: boolean; isWeekend: boolean; compact?: boolean;
  deals: Deal[]; teamById: Map<string, Team>; activeTeamColor?: string;
  onDealClick: (d: Deal) => void;
  onMore: (deals: Deal[]) => void;
  /** Клик по ячейке дня — открыть большое окно со списком игр этого дня. */
  onDayClick?: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell:${day}`,
    data: { type: 'cell', day },
  });
  const d = new Date(day);
  const maxItems = compact ? 5 : 3;
  const [moreOpen, setMoreOpen] = useState(false);
  const shiftMs = useTzShiftMs();
  const uid = useStore((s) => s.currentUser?.employee_id) ?? null;
  const formats = useStore((s) => s.game_formats);
  const cellFmt = (ts: number) => formatHM(ts + shiftMs);

  // Клик по ячейке (пустому месту) открывает список игр дня. Создание игры — только
  // кнопкой «Добавить» в шапке; двойной клик по ячейке игру БОЛЬШЕ НЕ создаёт (убрано как
  // лишнее — параметры всё равно надо заполнять руками, функция путала).
  // Клик по чипу игры, по числу дня и по «+N» сюда не долетает — там stopPropagation.
  const handleCellClick = () => { onDayClick?.(); };

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'border-r border-b border-line/60 p-1 relative flex flex-col transition-colors cursor-pointer',
        compact ? 'min-h-[160px]' : 'min-h-[120px]',
        isOtherMonth && 'bg-surface-2/40',
        isWeekend && !isOtherMonth && 'bg-warm-coral/[0.03]',
        isOver && 'bg-accent-soft/40 ring-2 ring-inset ring-accent',
      )}
      style={isOver && activeTeamColor ? { backgroundColor: activeTeamColor + '20' } : undefined}
      onClick={handleCellClick}
    >
      <div className="flex items-center justify-between px-1 mb-0.5">
        {/* Число дня — кнопка: клик открывает большое окно со списком игр дня.
            stopPropagation, чтобы не сработали двойной клик по ячейке (создать игру)
            и клик по фону. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDayClick?.(); }}
          onDoubleClick={(e) => e.stopPropagation()}
          title="Показать игры этого дня"
          className={cn(
            'text-xs font-bold tabular-nums cursor-pointer hover:opacity-70 transition-opacity',
            isOtherMonth ? 'text-ink-3' : isToday ? 'text-accent' : 'text-ink-1',
            isToday && 'bg-accent text-white w-5 h-5 rounded-full grid place-items-center',
          )}
        >
          {d.getDate()}
        </button>
        {deals.length > 0 && (
          <span className="text-[9px] text-ink-3 tabular-nums">{deals.length}</span>
        )}
      </div>
      <div className="flex-1 space-y-0.5 overflow-hidden">
        {deals.slice(0, maxItems).map((deal) => (
          <DraggableEventChip
            key={deal.id}
            deal={deal}
            team={deal.team_id ? teamById.get(deal.team_id) : null}
            onClick={() => onDealClick(deal)}
          />
        ))}
        {deals.length > maxItems && (
          <button
            onClick={(e) => { e.stopPropagation(); setMoreOpen((v) => !v); }}
            className="text-[10px] text-accent font-semibold hover:underline px-1"
          >
            +{deals.length - maxItems}
          </button>
        )}
      </div>

      {/* Раскрытие дня: все события списком (как в оригинале), кликабельны. */}
      {moreOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMoreOpen(false); }} />
          <div className="absolute z-50 left-1 right-1 top-7 menu p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-3 py-2 bg-surface-2 border-b border-line">
              <span className="text-xs font-semibold text-ink-2 tabular-nums">{d.getDate()} {MONTHS_FULL[d.getMonth()]} {d.getFullYear()} г.</span>
              <button className="icon-btn !w-6 !h-6" onClick={() => setMoreOpen(false)}><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {deals.map((deal) => {
                const t = deal.team_id ? teamById.get(deal.team_id) : null;
                const time = deal.event.start_at != null
                  ? `${cellFmt(deal.event.start_at)}${deal.event.end_at != null ? `-${cellFmt(deal.event.end_at)}` : ''}`
                  : '—';
                return (
                  <button key={deal.id} onClick={() => { onDealClick(deal); setMoreOpen(false); }}
                    className="w-full flex items-start gap-1.5 px-3 py-1 hover:bg-surface-2 text-left text-[11px]">
                    {isOwnDeal(deal, uid, t) && <OwnDot className="mt-1" />}
                    {t && <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: t.color }} title={t.name} />}
                    <span className="flex-shrink-0">{dealEmoji(deal, formats)}</span>
                    <EventBadge deal={deal} compact className="mt-0.5" />
                    {/* Название и время — в СТОЛБИК. Раньше стояли в одну строку, но ширина
                        окна = ширине ячейки дня (~190px), а номер/значок/маркер/время не
                        сжимаются — название схлопывалось до одного символа. min-w-0 нужен,
                        иначе flex-элемент не даёт truncate сработать. leading-[1.2] держит
                        строку компактной: две строки почти не выше прежней одной. */}
                    <span className="flex-1 min-w-0 leading-[1.2]">
                      <span className="block truncate text-ink-1 font-medium" title={dealTitle(deal)}><DealLabel deal={deal} /></span>
                      <span className="block text-[10px] text-ink-3 tabular-nums">{time}</span>
                    </span>
                    <MarkerBadge deal={deal} className="!w-3.5 !h-3.5 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── DAY view ───────────────────────────────────────────

// Склонение слова «час» по числу часов.
function hourWord(h: number): string {
  if (!Number.isInteger(h)) return 'часа';
  const n = h % 100;
  if (n >= 11 && n <= 14) return 'часов';
  const d = h % 10;
  if (d === 1) return 'час';
  if (d >= 2 && d <= 4) return 'часа';
  return 'часов';
}

// Название события: своё имя (event.title), иначе «Выезд 1.5 часа» / «Студия 1 час» (место + длительность).
function dealTitle(d: Deal): string {
  if (d.event.title && d.event.title.trim()) return d.event.title;
  const place = d.event.place === 'outside' ? 'Выезд' : 'Студия';
  const dur = d.event.duration_minutes
    ?? (d.event.start_at != null && d.event.end_at != null ? Math.round((d.event.end_at - d.event.start_at) / 60_000) : 60);
  const h = dur / 60;
  const hStr = Number.isInteger(h) ? String(h) : String(h).replace('.', '.');
  return `${place} ${hStr} ${hourWord(h)}`;
}

/**
 * Подпись игры в строке/чипе: `[ведущий · ][формат · ]название`.
 * Ведущий (event.preferred_host) и формат (по game_format_id из справочника) —
 * приглушённым цветом; название — как раньше (dealTitle). Пустые части и лишние
 * разделители не показываем. Рендерит инлайновые span'ы, поэтому truncate/overflow
 * родительского контейнера продолжают работать в тесных чипах.
 */
function DealLabel({ deal, full = false }: { deal: Deal; full?: boolean }) {
  const formats = useStore((s) => s.game_formats);
  const formatName = useMemo(() => {
    const id = deal.event.game_format_id;
    if (!id) return '';
    return formats.find((f) => f.id === id)?.name ?? '';
  }, [formats, deal.event.game_format_id]);
  // В тесных видах (месяц-сетка / «Месяц» / «Ресурсы» / попап) — только название (по просьбе
  // заказчика: там текст не помещается). Ведущего и формат показываем только в ПРОСТОРНЫХ
  // видах «День» и «График» (full=true), где смотрят в основном и место есть.
  if (!full) return <>{dealTitle(deal)}</>;
  const host = deal.event.preferred_host?.trim() ?? '';
  return (
    <>
      {host && <span className="text-ink-3 font-normal">{host} · </span>}
      {formatName && <span className="text-ink-3 font-normal">{formatName} · </span>}
      {dealTitle(deal)}
    </>
  );
}

const DOW_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

// Маленький квадратный бейдж команды с её номером и цветом.
function TeamBadge({ team, num }: { team: Team; num: number }) {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold text-white flex-shrink-0"
      style={{ backgroundColor: team.color }} title={team.name}>
      {team.icon || num}
    </span>
  );
}

// Одна строка списка игр дня. Общая для вида «День» и для окна-списка (клик по числу
// в сетке), чтобы вёрстка строки была ровно одна. index — порядковый номер (#1, #2…),
// navIds — соседи для листания карточки, fmt — форматтер времени с учётом часового сдвига.
function DayDealRow({ deal, index, navIds, teamById, teamNum, uid, fmt, onDealClick }: {
  deal: Deal; index: number; navIds: string[];
  teamById: Map<string, Team>; teamNum: Map<string, number>;
  uid: string | null;
  fmt: (ts: number) => string;
  onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const team = deal.team_id ? teamById.get(deal.team_id) : null;
  const placeIcon = deal.event.place === 'outside' ? '🚗' : '🏠';
  const formats = useStore((s) => s.game_formats);
  return (
    <button onClick={() => onDealClick(deal, navIds)}
      className="w-full flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 px-4 py-2.5 border-b border-line/60 last:border-b-0 hover:bg-accent-soft/30 transition-colors text-left">
      {isOwnDeal(deal, uid, team) ? <OwnDot /> : <span className="w-2 flex-shrink-0" />}
      {!deal.bitrix_id && <ManualDot />}
      <span className="text-xs text-ink-3 tabular-nums w-6 flex-shrink-0">#{index + 1}</span>
      <span className="text-sm text-ink-1 tabular-nums flex-shrink-0 sm:w-[104px]">
        {deal.event.start_at != null ? `${fmt(deal.event.start_at)}${deal.event.end_at != null ? `-${fmt(deal.event.end_at)}` : ''}` : '—'}
      </span>
      <span className="text-line hidden sm:inline">|</span>
      {team && <TeamBadge team={team} num={teamNum.get(team.id) ?? 0} />}
      <span className="flex items-center gap-2 flex-shrink-0 ml-auto sm:ml-0 sm:order-2">
        <PrebookingBadge deal={deal} />
        <MarkerBadge deal={deal} />
        <span className="text-sm">{placeIcon}</span>
      </span>
      {/* На телефоне название уезжает на вторую строку целиком (иначе сжималось до одной
          буквы), на компьютере — как было: в той же строке, растягивается по остатку. */}
      <span className="order-last basis-full min-w-0 flex items-center gap-2 sm:order-1 sm:basis-0 sm:flex-1">
        <span className="text-base flex-shrink-0">{dealEmoji(deal, formats)}</span>
        <EventBadge deal={deal} />
        <span className="text-sm font-semibold text-ink-1 truncate min-w-0" title={dealTitle(deal)}><DealLabel deal={deal} full /></span>
      </span>
    </button>
  );
}

// ── DAY view — простой список событий дня (как в эталоне) ──
function DayView({ day, setDay, deals, teamById, teamNum, filterBranchId, onDealClick }: {
  day: number; setDay: (n: number) => void;
  deals: Deal[]; teamById: Map<string, Team>; teamNum: Map<string, number>;
  filterBranchId: string;
  /** navIds — соседи для листания карточки: события вида в экранном порядке. */
  onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const shiftMs = useTzShiftMs();
  const uid = useStore((s) => s.currentUser?.employee_id) ?? null;
  const dayDate = new Date(day);
  const dayEnd = day + 24 * 3600_000;
  const fmt = (ts: number) => formatHM(ts + shiftMs);

  const dayDeals = useMemo(() => deals.filter((d) => {
    if (d.status === 'refused' || isPrebookingExpired(d)) return false;
    if (!dealInBranch(d, filterBranchId)) return false;
    const s = d.event.start_at ?? d.event.event_date;
    return s != null && s + shiftMs >= day && s + shiftMs < dayEnd;
  }).sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0)), [deals, filterBranchId, day, dayEnd, shiftMs]);

  const navIds = useMemo(() => dayDeals.map((d) => d.id), [dayDeals]);

  const dateLabel = `${String(dayDate.getDate()).padStart(2, '0')}.${String(dayDate.getMonth() + 1).padStart(2, '0')}.${String(dayDate.getFullYear()).slice(2)} ${DOW_RU[dayDate.getDay()]}`;

  return (
    <div className="px-1 py-2 sm:p-4">
      <div className="panel overflow-hidden rounded-none sm:rounded-2xl">
        <div className="px-4 py-2 bg-surface-2/50 text-xs text-ink-3 tabular-nums border-b border-line">{dateLabel}</div>
        {dayDeals.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-ink-3">На этот день событий нет.</div>
        ) : dayDeals.map((deal, i) => (
          <DayDealRow
            key={deal.id}
            deal={deal}
            index={i}
            navIds={navIds}
            teamById={teamById}
            teamNum={teamNum}
            uid={uid}
            fmt={fmt}
            onDealClick={onDealClick}
          />
        ))}
      </div>
    </div>
  );
}

// Полное название дня недели (для правой части полоски-заголовка дня).
const DOW_FULL = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

// ── MONTH view — список событий всего месяца, разбитый по дням (как «День», но за месяц) ──
function MonthView({ month, deals, teamById, teamNum, filterBranchId, onDealClick }: {
  month: number; deals: Deal[]; teamById: Map<string, Team>; teamNum: Map<string, number>;
  filterBranchId: string; onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const shiftMs = useTzShiftMs();
  const uid = useStore((s) => s.currentUser?.employee_id) ?? null;
  const branches = useStore((s) => s.branches);
  const formats = useStore((s) => s.game_formats);
  const fmt = (ts: number) => formatHM(ts + shiftMs);
  // Город филиала сделки — добавляется к текстовому адресу при поиске на карте.
  const cityOf = (d: Deal): string | null => {
    const b = d.branch_id ? branches.find((x) => x.id === d.branch_id) : null;
    return b ? (b.city || b.name) : null;
  };

  // Группируем события месяца по дням (только дни, где есть события).
  const groups = useMemo(() => {
    const first = new Date(month); first.setDate(1); first.setHours(0, 0, 0, 0);
    const monthStart = first.getTime();
    const next = new Date(first); next.setMonth(next.getMonth() + 1);
    const monthEnd = next.getTime();

    const byDay = new Map<number, Deal[]>();
    for (const d of deals) {
      if (d.status === 'refused' || isPrebookingExpired(d)) continue;
      if (!dealInBranch(d, filterBranchId)) continue;
      const s = d.event.start_at ?? d.event.event_date;
      if (s == null) continue;
      const adj = s + shiftMs;
      if (adj < monthStart || adj >= monthEnd) continue;
      const key = startOfDay(adj);
      const arr = byDay.get(key) ?? [];
      arr.push(d);
      byDay.set(key, arr);
    }
    return [...byDay.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([day, list]) => ({
        day,
        deals: list.sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0)),
      }));
  }, [deals, filterBranchId, month, shiftMs]);

  // Порядок листания — сквозной по всему месяцу (день за днём, внутри дня по времени).
  const navIds = useMemo(() => groups.flatMap((g) => g.deals.map((d) => d.id)), [groups]);

  return (
    <div className="p-4">
      <div className="panel overflow-hidden">
        {groups.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-ink-3">В этом месяце событий нет.</div>
        ) : groups.map(({ day, deals: dayDeals }) => {
          const dd = new Date(day);
          const dateLabel = `${String(dd.getDate()).padStart(2, '0')}.${String(dd.getMonth() + 1).padStart(2, '0')}.${String(dd.getFullYear()).slice(2)} ${DOW_RU[dd.getDay()]}`;
          return (
            <div key={day}>
              {/* полоска-заголовок дня */}
              <div className="flex items-center justify-between px-4 py-1.5 bg-surface-2/60 border-y border-line text-xs">
                <span className="text-ink-2 font-medium tabular-nums">{dateLabel}</span>
                <span className="text-ink-3">{DOW_FULL[dd.getDay()]}</span>
              </div>
              {dayDeals.map((deal, i) => {
                const team = deal.team_id ? teamById.get(deal.team_id) : null;
                const placeIcon = deal.event.place === 'outside' ? '🚗' : '🏠';
                const addr = deal.event.address;
                return (
                  <button key={deal.id} onClick={() => onDealClick(deal, navIds)}
                    className="w-full flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1 px-4 py-2.5 border-b border-line/60 hover:bg-accent-soft/30 transition-colors text-left">
                    {isOwnDeal(deal, uid, team) ? <OwnDot className="mt-1.5" /> : <span className="w-2 flex-shrink-0" />}
                    {!deal.bitrix_id && <ManualDot className="mt-1.5" />}
                    <span className="text-xs text-ink-3 tabular-nums w-6 flex-shrink-0 mt-0.5">#{i + 1}</span>
                    <span className="text-sm text-ink-1 tabular-nums flex-shrink-0 mt-0.5 sm:w-[104px]">
                      {deal.event.start_at != null ? `${fmt(deal.event.start_at)}${deal.event.end_at != null ? `-${fmt(deal.event.end_at)}` : ''}` : '—'}
                    </span>
                    {team && <span className="mt-0.5"><TeamBadge team={team} num={teamNum.get(team.id) ?? 0} /></span>}
                    <MarkerBadge deal={deal} className="mt-1 sm:order-2" />
                    {/* На телефоне название с адресом — отдельной строкой (иначе сжималось в одну букву). */}
                    <span className="order-last basis-full min-w-0 flex items-start gap-2 sm:order-1 sm:basis-0 sm:flex-1">
                      <span className="text-base flex-shrink-0 mt-0.5">{dealEmoji(deal, formats)}</span>
                      <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink-1 truncate" title={dealTitle(deal)}>
                        <EventBadge deal={deal} className="mr-1 align-middle" /><DealLabel deal={deal} />
                      </span>
                      {addr && (
                        <span className="block text-[11px] text-ink-3 truncate">
                          {addr}
                          {' '}
                          <a
                            href={mapUrl(addr, deal.event.geo, cityOf(deal))}
                            target="_blank" rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-accent hover:underline"
                          >2ГИС</a>
                        </span>
                      )}
                      </span>
                    </span>
                    <span className="text-sm flex-shrink-0 mt-0.5 ml-auto sm:ml-0 sm:order-3">{placeIcon}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── GRAPH view (расстановка сотрудников по событиям дня, эталон ShowToday) ──

// Роли на событии (как в эталоне): эмодзи + короткое имя для режима «Название роли».
const EVENT_ROLES: { key: string; label: string; emoji: string; abbr: string }[] = [
  { key: 'vedushiy',   label: 'Ведущий 1', emoji: '🎤', abbr: 'ВЕД1' },
  { key: 'vedushiy2',  label: 'Ведущий 2', emoji: '🎤', abbr: 'ВЕД2' },
  { key: 'sovedushiy', label: 'Соведущий', emoji: '👯', abbr: 'СОВ' },
  { key: 'didjey',     label: 'Диджей',    emoji: '🎧', abbr: 'ДИД' },
  { key: 'hostes',     label: 'Хостес',    emoji: '💁', abbr: 'ХОС' },
  { key: 'voditel',    label: 'Водитель',  emoji: '🚗', abbr: 'ВОД' },
  { key: 'stajer',     label: 'Стажёр',    emoji: '🎓', abbr: 'СТАЖ' },
];
const ROLE_BY_KEY = new Map(EVENT_ROLES.map((r) => [r.key, r]));
// Старые роли сотрудников из демо приводим к ролям события, чтобы значки выглядели осмысленно.
const ROLE_ALIAS: Record<string, string> = { host: 'vedushiy', tech: 'didjey', admin: 'vedushiy', manager: 'hostes', event_manager: 'sovedushiy' };
function roleInfo(role: string) {
  return ROLE_BY_KEY.get(ROLE_ALIAS[role] ?? role) ?? { key: role, label: role, emoji: '👤', abbr: role.slice(0, 3).toUpperCase() };
}

// Статус → метка воронки заказчика (как на эталоне).
const FUNNEL: Record<string, { label: string; cls: string }> = {
  new:          { label: 'Предбронь', cls: 'bg-amber-100 text-amber-700' },
  in_work:      { label: 'Бронь',     cls: 'bg-accent-soft text-accent' },
  paid_partial: { label: 'Бронь',     cls: 'bg-accent-soft text-accent' },
  paid:         { label: 'Проведена', cls: 'bg-surface-2 text-ink-2' },
  done:         { label: 'Проведена', cls: 'bg-surface-2 text-ink-2' },
  refused:      { label: 'Отмена',    cls: 'bg-status-refused/20 text-status-refused' },
};

/** Значок маркера на карточке: ✓ подтверждено / 💾 финансово проверено. null — не помечать. */
function dealMarker(d: Deal, mf: 'none' | 'confirmed' | 'finance'): null | { icon: string; title: string } {
  if (mf === 'confirmed' && d.confirmed) return { icon: '✓', title: 'Подтверждено' };
  if (mf === 'finance') {
    if (isFinanceVerified(d)) return { icon: '💾', title: 'Финансово проверено' };
  }
  return null;
}

/** Значок маркера (читает выбранный маркер из стора). Если маркер выбран: на подходящих —
 *  ✓/💾 (зелёный), на НЕподходящих — красный ✗. Если маркер «Без маркеров» — ничего. */
function MarkerBadge({ deal, className }: { deal: Deal; className?: string }) {
  const mf = useStore((s) => s.markerFilter);
  if (mf === 'none') return null;
  const m = dealMarker(deal, mf);
  return (
    <span title={m ? m.title : 'Нет маркера'}
      className={cn('inline-flex items-center justify-center text-[10px] leading-none rounded-full text-white w-4 h-4 flex-shrink-0',
        m ? 'bg-status-paid' : 'bg-status-refused', className)}>
      {m ? m.icon : '✗'}
    </span>
  );
}

function EmpAvatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span className="rounded-full grid place-items-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, backgroundColor: `hsl(${hashHue(name)}, 55%, 48%)` }}>
      {initialsOf(name)}
    </span>
  );
}

type StaffA = Deal['staff_assignments'][number];  // единый тип состава (см. types.ts), вместе со слепком имени

// Чип назначенного сотрудника: клик открывает меню ролей (как в эталоне) + «Удалить».
function GraphEmpChip({ emp, role, fullName, showRoleText, highlighted, onHover, onPickRole, onRemove }: {
  emp?: Employee; role: string; fullName: boolean; showRoleText: boolean; highlighted: boolean;
  onHover: (id: string | null) => void; onPickRole: (role: string) => void; onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ri = roleInfo(role);
  const name = emp?.full_name ?? 'неизвестно';
  return (
    <div className="relative">
      <button
        onMouseEnter={() => emp && onHover(emp.id)} onMouseLeave={() => onHover(null)}
        onClick={() => setOpen((o) => !o)}
        className={cn('flex items-center gap-1.5 border text-left transition-colors',
          fullName ? 'w-full pl-1 pr-2.5 h-9 rounded-lg' : 'px-1 h-7 rounded-full',
          highlighted ? 'border-accent bg-accent-soft/60'
            : fullName ? 'border-line/70 bg-surface-1 hover:bg-surface-2' : 'border-transparent hover:bg-surface-2')}
      >
        <EmpAvatar name={name} size={fullName ? 24 : 22} />
        {fullName && <span className="text-xs text-ink-1 truncate flex-1">{name}</span>}
        <span className="text-[11px] text-ink-3 flex-shrink-0 font-medium" title={ri.label}>
          {showRoleText ? ri.abbr : ri.emoji}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 top-9 left-0 menu min-w-[185px] overflow-hidden">
            {EVENT_ROLES.map((r) => (
              <button key={r.key} className={cn('menu-item !h-10', r.key === ri.key && 'bg-accent-soft text-accent font-medium')}
                onClick={() => { onPickRole(r.key); setOpen(false); }}>
                <span className="w-5 text-center text-base">{r.emoji}</span> {r.label}
              </button>
            ))}
            <div className="h-px bg-line my-1" />
            <button className="menu-item !h-10 text-status-refused hover:!bg-status-refused/10" onClick={() => { onRemove(); setOpen(false); }}>
              <X className="w-4 h-4" /> Удалить
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Ячейка «Сотрудник» — зона, куда можно бросить человека из ростера (нативный DnD).
function StaffDropCell({ staff, empById, fullName, showRoleText, hoverEmp, setHoverEmp, onDropEmp, onPickRole, onRemove }: {
  staff: StaffA[]; empById: Map<string, Employee>; fullName: boolean; showRoleText: boolean;
  hoverEmp: string | null; setHoverEmp: (id: string | null) => void;
  onDropEmp: (empId: string) => void; onPickRole: (empId: string, role: string) => void; onRemove: (empId: string) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData('text/plain'); if (id) onDropEmp(id); }}
      className={cn('min-h-[40px] rounded-lg p-1 flex gap-1', fullName ? 'flex-col' : 'flex-row flex-wrap items-center',
        over && 'ring-2 ring-accent bg-accent-soft/30')}
    >
      {staff.length === 0 && <span className="text-[11px] text-ink-3/50 italic self-center px-1">перетащи сюда</span>}
      {staff.map((s) => (
        <GraphEmpChip key={s.employee_id} emp={empById.get(s.employee_id)} role={s.role}
          fullName={fullName} showRoleText={showRoleText}
          highlighted={hoverEmp === s.employee_id}
          onHover={setHoverEmp}
          onPickRole={(r) => onPickRole(s.employee_id, r)}
          onRemove={() => onRemove(s.employee_id)} />
      ))}
    </div>
  );
}

function GraphView({ day, setDay, deals, employees, teamById, teamNum, filterBranchId, onDealClick }: {
  day: number; setDay: (n: number) => void;
  deals: Deal[]; employees: Employee[]; teamById: Map<string, Team>; teamNum: Map<string, number>;
  filterBranchId: string; onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const shiftMs = useTzShiftMs();
  const formats = useStore((s) => s.game_formats);
  const [roster, setRoster] = useState('');
  const [fullName, setFullName] = useState(true);     // настройка «Полное имя»
  const [showRoleText, setShowRoleText] = useState(false); // настройка «Название роли» (по умолчанию — эмодзи)
  const [gearOpen, setGearOpen] = useState(false);
  const [hoverEmp, setHoverEmp] = useState<string | null>(null);
  const [empFilter, setEmpFilter] = useState<string | null>(null);   // фильтр таблицы по сотруднику
  const [teamFilter, setTeamFilter] = useState<string | null>(null); // фильтр таблицы по команде
  const [empMenu, setEmpMenu] = useState(false);
  const [teamMenu, setTeamMenu] = useState(false);
  const [draft, setDraft] = useState<Record<string, StaffA[]>>({});  // несохранённая расстановка

  const dayDate = new Date(day);
  const dayEnd = day + 24 * 3600_000;
  const empById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const fmt = (ts: number) => formatHM(ts + shiftMs);

  const dayDeals = useMemo(() => deals.filter((d) => {
    if (d.status === 'refused' || isPrebookingExpired(d)) return false;
    if (!dealInBranch(d, filterBranchId)) return false;
    const s = d.event.start_at ?? d.event.event_date;
    return s != null && s + shiftMs >= day && s + shiftMs < dayEnd;
  }).sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0)), [deals, filterBranchId, day, dayEnd, shiftMs]);

  const staffOf = (d: Deal): StaffA[] => draft[d.id] ?? d.staff_assignments;

  // Сколько событий у каждого сотрудника в этот день (с учётом несохранённой расстановки).
  const load = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dayDeals) for (const sa of staffOf(d)) m.set(sa.employee_id, (m.get(sa.employee_id) ?? 0) + 1);
    return m;
  }, [dayDeals, draft]);

  // Сотрудники города (по выбранному филиалу).
  const cityEmps = useMemo(() => employees.filter((e) => e.is_active !== false && (filterBranchId === 'all' || e.branch_id === filterBranchId)), [employees, filterBranchId]);
  const rosterList = cityEmps
    .filter((e) => roster.trim() === '' || e.full_name.toLowerCase().includes(roster.toLowerCase()))
    .sort((a, b) => (load.get(b.id) ?? 0) - (load.get(a.id) ?? 0) || a.full_name.localeCompare(b.full_name));

  // Таблица с учётом фильтров по столбцам.
  const tableDeals = dayDeals.filter((d) => {
    if (teamFilter && (d.team_id ?? null) !== teamFilter) return false;
    if (empFilter && !staffOf(d).some((s) => s.employee_id === empFilter)) return false;
    return true;
  });
  // Соседи для листания карточки — строки таблицы (уже с учётом фильтров вида).
  const navIds = tableDeals.map((d) => d.id);

  // Кто был в этот день (для фильтра по сотруднику) и какие команды.
  const presentEmps = cityEmps.filter((e) => (load.get(e.id) ?? 0) > 0);
  const presentTeams = useMemo(() => {
    const ids = [...new Set(dayDeals.map((d) => d.team_id).filter(Boolean) as string[])];
    return ids.map((id) => teamById.get(id)).filter(Boolean) as Team[];
  }, [dayDeals, teamById]);

  // Изменения расстановки (в черновик; сохраняются по дискете).
  const setStaff = (dealId: string, next: StaffA[]) => setDraft((p) => ({ ...p, [dealId]: next }));
  const dropEmp = (deal: Deal, empId: string) => {
    const cur = staffOf(deal);
    if (cur.some((s) => s.employee_id === empId)) return;
    // Слепок имени — см. types.ts (staff_assignments): чтобы удаление карточки сотрудника
    // не превращало прошедшую игру в состав «неизвестно».
    setStaff(deal.id, [...cur, { employee_id: empId, role: 'vedushiy', employee_name: empById.get(empId)?.full_name }]);
  };
  const pickRole = (deal: Deal, empId: string, role: string) => setStaff(deal.id, staffOf(deal).map((s) => s.employee_id === empId ? { ...s, role } : s));
  const removeEmp = (deal: Deal, empId: string) => setStaff(deal.id, staffOf(deal).filter((s) => s.employee_id !== empId));
  const resetStaff = () => { const next: Record<string, StaffA[]> = {}; for (const d of dayDeals) next[d.id] = []; setDraft((p) => ({ ...p, ...next })); };

  const dirty = Object.keys(draft).length > 0;
  const save = async () => {
    for (const id of Object.keys(draft)) {
      const deal = deals.find((x) => x.id === id);
      if (deal) await useStore.getState().saveDeal({ ...deal, staff_assignments: draft[id] });
    }
    setDraft({});
  };

  const Toggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
    <button onClick={onClick} className={cn('w-9 h-5 rounded-full relative transition-colors flex-shrink-0', on ? 'bg-accent' : 'bg-surface-3')}>
      <span className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all', on ? 'left-[18px]' : 'left-0.5')} />
    </button>
  );

  return (
    <div className="p-4 h-full flex flex-col">
      {/* навигация + действия */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button className="icon-btn !w-8 !h-8" onClick={() => setDay(addDays(day, -1))} title="Предыдущий день"><ChevronLeft className="w-4 h-4" /></button>
        <button className="px-3 h-8 text-xs font-medium rounded-full bg-surface-2 hover:bg-surface-3 transition-colors" onClick={() => setDay(startOfDay(Date.now()))}>Сегодня</button>
        <button className="icon-btn !w-8 !h-8" onClick={() => setDay(addDays(day, 1))} title="Следующий день"><ChevronRight className="w-4 h-4" /></button>
        <h2 className="text-sm font-semibold text-ink-1 ml-2">{dayDate.getDate()} {MONTHS_FULL[dayDate.getMonth()]} {dayDate.getFullYear()} г.</h2>
        <div className="flex-1" />
        <button onClick={save} disabled={!dirty}
          className={cn('icon-btn', dirty ? 'text-accent' : 'opacity-40 cursor-default')} title="Сохранить расстановку">
          <Save className="w-4 h-4" />
        </button>
        <div className="relative">
          <button className={cn('icon-btn', gearOpen && 'bg-accent-soft text-accent')} onClick={() => setGearOpen((v) => !v)} title="Настройки"><SettingsIcon className="w-4 h-4" /></button>
          {gearOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setGearOpen(false)} />
              <div className="absolute right-0 top-11 z-40 menu min-w-[220px] px-1">
                <div className="menu-item rounded-lg justify-between">
                  <span>Полное имя</span><Toggle on={fullName} onClick={() => setFullName((v) => !v)} />
                </div>
                <div className="menu-item rounded-lg justify-between">
                  <span>Название роли</span><Toggle on={showRoleText} onClick={() => setShowRoleText((v) => !v)} />
                </div>
                <div className="h-px bg-line my-1" />
                <button className="menu-item rounded-lg" onClick={() => { resetStaff(); setGearOpen(false); }}>Сбросить сотрудников</button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-3 min-h-0">
        {/* таблица событий */}
        <div className="panel flex-1 overflow-auto min-h-[50vh] lg:min-h-0">
          <table className="w-full min-w-[560px] lg:min-w-0 text-sm">
            <thead className="sticky top-0 bg-surface-2 z-20">
              <tr className="text-xs text-ink-3 text-left border-b border-line">
                <th className="px-3 py-2 font-medium w-[140px] border-r border-line/70">Время</th>
                <th className="px-3 py-2 font-medium border-r border-line/70">Название</th>
                <th className="px-2 py-1.5 font-medium w-[240px] relative border-r border-line/70">
                  <button className="w-full flex items-center justify-between gap-1 px-2 h-7 rounded bg-surface-3/50 hover:bg-surface-3 transition-colors text-ink-2" onClick={() => setEmpMenu((v) => !v)}>
                    <span className="truncate">Сотрудник{empFilter && <span className="text-accent">: {empById.get(empFilter)?.full_name}</span>}</span>
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                  </button>
                  {empFilter && <button className="absolute right-9 top-1/2 -translate-y-1/2 text-ink-3 hover:text-status-refused z-10" onClick={() => setEmpFilter(null)} title="Сбросить фильтр"><X className="w-3.5 h-3.5" /></button>}
                  {empMenu && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setEmpMenu(false)} />
                      <div className="absolute z-40 top-9 left-0 menu min-w-[200px] max-h-[320px] overflow-y-auto normal-case">
                        {presentEmps.length === 0 && <div className="px-3 py-2 text-xs text-ink-3 italic">в этот день никого</div>}
                        {presentEmps.map((e) => (
                          <button key={e.id} className="menu-item rounded-lg text-xs" onClick={() => { setEmpFilter(e.id); setEmpMenu(false); }}>
                            <EmpAvatar name={e.full_name} size={20} /> <span className="truncate flex-1 text-left">{e.full_name}</span>
                            <span className="text-ink-3 tabular-nums">{load.get(e.id)}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </th>
                <th className="px-2 py-1.5 font-medium w-[190px] relative">
                  <button className="w-full flex items-center justify-between gap-1 px-2 h-7 rounded bg-surface-3/50 hover:bg-surface-3 transition-colors text-ink-2" onClick={() => setTeamMenu((v) => !v)}>
                    <span className="truncate">Календарь{teamFilter && <span className="text-accent">: {teamById.get(teamFilter)?.name}</span>}</span>
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                  </button>
                  {teamFilter && <button className="absolute right-9 top-1/2 -translate-y-1/2 text-ink-3 hover:text-status-refused z-10" onClick={() => setTeamFilter(null)} title="Сбросить фильтр"><X className="w-3.5 h-3.5" /></button>}
                  {teamMenu && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setTeamMenu(false)} />
                      <div className="absolute z-40 top-9 left-0 menu min-w-[180px] max-h-[320px] overflow-y-auto normal-case">
                        {presentTeams.length === 0 && <div className="px-3 py-2 text-xs text-ink-3 italic">нет команд</div>}
                        {presentTeams.map((t) => (
                          <button key={t.id} className="menu-item rounded-lg text-xs" onClick={() => { setTeamFilter(t.id); setTeamMenu(false); }}>
                            <TeamBadge team={t} num={teamNum.get(t.id) ?? 0} /> <span className="truncate flex-1 text-left">{t.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {tableDeals.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-ink-3">На этот день событий нет.</td></tr>
              )}
              {tableDeals.map((d, i) => {
                const team = d.team_id ? teamById.get(d.team_id) : null;
                const f = FUNNEL[d.status];
                const placeIcon = d.event.place === 'outside' ? '🚗' : '🏠';
                const rowHi = hoverEmp != null && staffOf(d).some((s) => s.employee_id === hoverEmp);
                return (
                  <tr key={d.id} className={cn('border-b border-line/60 align-top transition-colors', rowHi ? 'bg-accent-soft/25' : 'hover:bg-surface-2/40')}>
                    <td className="px-3 py-2 tabular-nums text-xs text-ink-1 whitespace-nowrap border-r border-line/70">
                      <span className="text-ink-3">#{i + 1}</span> {d.event.start_at != null ? `${fmt(d.event.start_at)}${d.event.end_at != null ? ` - ${fmt(d.event.end_at)}` : ''}` : '—'}
                    </td>
                    <td className="px-3 py-2 border-r border-line/70">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <button onClick={() => onDealClick(d, navIds)} title={dealTitle(d)} className="text-left font-semibold text-ink-1 hover:text-accent"><DealLabel deal={d} full /></button>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {f && <span className={cn('px-1.5 h-5 inline-flex items-center rounded text-[10px] font-medium', f.cls)}>{f.label}</span>}
                            {!d.bitrix_id && <ManualDot />}
                            <EventBadge deal={d} />
                            <span className="text-sm">{dealEmoji(d, formats)}</span>
                            <span className="text-sm">{placeIcon}</span>
                            <MarkerBadge deal={d} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 border-r border-line/70">
                      <StaffDropCell staff={staffOf(d)} empById={empById} fullName={fullName} showRoleText={showRoleText}
                        hoverEmp={hoverEmp} setHoverEmp={setHoverEmp}
                        onDropEmp={(empId) => dropEmp(d, empId)}
                        onPickRole={(empId, role) => pickRole(d, empId, role)}
                        onRemove={(empId) => removeEmp(d, empId)} />
                    </td>
                    <td className="px-3 py-2">
                      {team ? (
                        <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 h-7 rounded-full text-[12px] font-medium"
                          style={{ backgroundColor: team.color + '1f', color: team.color }}>
                          <TeamBadge team={team} num={teamNum.get(team.id) ?? 0} /> {team.name}
                        </span>
                      ) : <span className="text-[11px] text-ink-3/60 italic">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* правая панель — ростер сотрудников города */}
        <div className="panel w-full lg:w-64 flex-shrink-0 flex flex-col overflow-hidden max-h-[40vh] lg:max-h-none">
          <div className="p-2 border-b border-line">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3" />
              <input value={roster} onChange={(e) => setRoster(e.target.value)} placeholder="Поиск сотрудника" className="input h-9 pl-8 text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {rosterList.length === 0 && <div className="text-[11px] text-ink-3 italic text-center py-4">Никого не найдено</div>}
            {rosterList.map((e) => {
              const cnt = load.get(e.id) ?? 0;
              const hi = hoverEmp === e.id;
              return (
                <div key={e.id}
                  draggable
                  onDragStart={(ev) => { ev.dataTransfer.setData('text/plain', e.id); ev.dataTransfer.effectAllowed = 'copy'; }}
                  onMouseEnter={() => setHoverEmp(e.id)} onMouseLeave={() => setHoverEmp(null)}
                  className={cn('flex items-center gap-2 pl-1 pr-3 h-10 rounded-lg cursor-grab active:cursor-grabbing transition-colors',
                    hi ? 'bg-accent-soft/60' : 'hover:bg-surface-2')}>
                  <EmpAvatar name={e.full_name} size={28} />
                  <span className="flex-1 truncate text-sm text-ink-1">{e.full_name}</span>
                  <span className={cn('text-xs tabular-nums', cnt > 0 ? 'text-ink-2 font-semibold' : 'text-ink-3')}>{cnt}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RESOURCES view (таймлайн по времени, как в эталоне ShowToday) ──────

// Ширина одного часа в пикселях на ленте «Ресурсы» (исп. и при расчёте сдвига времени перетаскиванием).
const RES_HOUR_W = 64;

type ResItem = { d: Deal; s: number; e: number; conflict: boolean };

// Перетаскиваемый блок события на ленте: тащить ↔ сдвиг времени, ↕ смена команды.
function ResEventBlock({ item, left, width, color, time, title, onClick }: {
  item: ResItem; left: number; width: number; color: string; time: string; title: string; onClick: () => void;
}) {
  const { d, conflict } = item;
  const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: `resdeal:${d.id}`, data: { type: 'resourceDeal', dealId: d.id },
  });
  const formats = useStore((s) => s.game_formats);
  return (
    <button ref={setNodeRef} {...attributes} {...listeners}
      onClick={(ev) => { ev.stopPropagation(); onClick(); }}
      className={cn(
        'absolute top-1 bottom-1 rounded-md px-1.5 flex items-center gap-1 text-left text-ink-1 overflow-hidden hover:brightness-110 transition-shadow cursor-grab active:cursor-grabbing',
        conflict && 'ring-2 ring-status-refused',
        isDragging && 'opacity-80 shadow-pop z-30',
      )}
      style={{
        left, width, backgroundColor: color + '40', borderLeft: `3px solid ${color}`,
        transform: CSS.Translate.toString(transform),
      }}
      title={title}>
      <span className="text-[11px] flex-shrink-0">{dealEmoji(d, formats)}</span>
      {!d.bitrix_id && <ManualDot />}
      <EventBadge deal={d} compact />
      <span className="text-[10px] font-medium truncate min-w-0"><DealLabel deal={d} /></span>
      <span className="flex items-center gap-1 ml-auto flex-shrink-0">
        <PrebookingBadge deal={d} />
        <MarkerBadge deal={d} className="!w-3.5 !h-3.5" />
        <span className="text-[9px] text-ink-3 tabular-nums">{time}</span>
      </span>
    </button>
  );
}

// Дорожка команды — зона, куда можно бросить сделку (назначить на эту команду + время по X).
function ResourceTrack({ items, color, teamId, day, trackW, hourTicks, toPx, breakMs, breakMinutes, fmt, onDealClick }: {
  items: ResItem[]; color: string | null; teamId: string | null; day: number;
  trackW: number; hourTicks: number[]; toPx: (ts: number) => number;
  breakMs: number; breakMinutes: number; fmt: (ts: number) => string; onDealClick: (d: Deal) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `resrow:${teamId ?? 'none'}:${day}`, data: { type: 'resourceRow', teamId, day } });
  const c = color ?? '#a09ac0';
  return (
    <div ref={setNodeRef} className={cn('relative py-1.5 flex-shrink-0', isOver && 'bg-accent-soft/30')} style={{ width: trackW }}>
      {/* вертикальная сетка часов */}
      {hourTicks.map((h) => (
        <div key={h} className="absolute top-0 bottom-0 border-l border-line/40" style={{ left: h * RES_HOUR_W }} />
      ))}
      {/* зоны перерыва (под событиями) */}
      {items.map(({ d, e }) => {
        const bl = toPx(e), bw = toPx(e + breakMs) - bl;
        if (bw <= 0) return null;
        return (
          <div key={`brk-${d.id}`} className="absolute top-2 bottom-2 rounded-sm pointer-events-none"
            style={{
              left: bl, width: bw, backgroundColor: c + '1f',
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.10) 3px, rgba(0,0,0,0.10) 6px)',
            }}
            title={`Перерыв ${breakMinutes} мин`} />
        );
      })}
      {/* блоки событий */}
      {items.map((it) => {
        const left = toPx(it.s), width = Math.max(8, toPx(it.e) - left);
        return (
          <ResEventBlock key={it.d.id} item={it} left={left} width={width} color={c}
            time={fmt(it.s)}
            title={it.d.contact_name || it.d.number}
            onClick={() => onDealClick(it.d)} />
        );
      })}
    </div>
  );
}

function ResourcesView({ day, setDay, teams, deals, enabledTeamIds, filterBranchId, breakMinutes, onDealClick }: {
  day: number; setDay: (n: number) => void;
  teams: Team[]; deals: Deal[]; enabledTeamIds: Set<string>;
  filterBranchId: string; breakMinutes: number;
  onDealClick: (d: Deal, navIds: string[]) => void;
}) {
  const dayDate = new Date(day);
  const dayEnd = day + 24 * 3600_000;
  const breakMs = breakMinutes * 60_000;
  // Сдвиг показа времени по выбранному в часах поясу.
  const shiftMs = useTzShiftMs();
  const fmt = (ts: number) => formatHM(ts + shiftMs);

  // Сделки выбранного дня (без отказов и истёкших предброней), с учётом фильтра филиала
  const dayDeals = useMemo(() => deals.filter((d) => {
    if (d.status === 'refused' || isPrebookingExpired(d)) return false;
    if (!dealInBranch(d, filterBranchId)) return false;
    const s = d.event.start_at ?? d.event.event_date;
    return s != null && s + shiftMs >= day && s + shiftMs < dayEnd;
  }), [deals, filterBranchId, day, dayEnd, shiftMs]);

  // Линейка часов — всегда полные сутки 0..24, фикс. ширина часа (лента листается по горизонтали).
  const HOUR_W = RES_HOUR_W;
  const trackW = 24 * HOUR_W;
  const hourTicks = Array.from({ length: 25 }, (_, h) => h); // 0..24
  // Пиксель-позиция момента на ленте (с учётом выбранного пояса).
  const toPx = (ts: number) => {
    const min = (ts + shiftMs - day) / 60_000;
    return Math.max(0, Math.min(trackW, (min / 60) * HOUR_W));
  };

  // Раскладка событий команды по времени + флаг конфликта (старт раньше «конец+перерыв» предыдущего)
  const layoutFor = (list: Deal[]) => {
    const sorted = [...list].sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0));
    let prevEnd: number | null = null;
    return sorted.map((d) => {
      const s = d.event.start_at ?? day + 18 * 3600_000;
      const e = d.event.end_at ?? s + 2 * 3600_000;
      const conflict = prevEnd != null && s < prevEnd + breakMs;
      prevEnd = e;
      return { d, s, e, conflict };
    });
  };

  const teamRows = teams.filter((t) => enabledTeamIds.has(t.id));
  const noTeam = dayDeals.filter((d) => !d.team_id);
  const dow = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'][dayDate.getDay()];

  // Соседи для листания карточки — дорожки сверху вниз, внутри дорожки по времени.
  const navIds = [
    ...teamRows.flatMap((t) => layoutFor(dayDeals.filter((d) => d.team_id === t.id)).map((it) => it.d.id)),
    ...layoutFor(noTeam).map((it) => it.d.id),
  ];

  const renderTrack = (list: Deal[], color: string | null, teamId: string | null) => (
    <ResourceTrack
      items={layoutFor(list)} color={color} teamId={teamId} day={day}
      trackW={trackW} hourTicks={hourTicks} toPx={toPx}
      breakMs={breakMs} breakMinutes={breakMinutes} fmt={fmt}
      onDealClick={(d) => onDealClick(d, navIds)}
    />
  );

  return (
    <div className="px-1 py-2 sm:p-4">
      <div className="panel overflow-hidden rounded-none sm:rounded-2xl">
        <div className="overflow-x-auto">
          <div style={{ width: 200 + trackW }}>
            {/* шапка часов */}
            <div className="flex border-b border-line bg-surface-1 sticky top-0 z-20">
              <div className="w-[104px] sm:w-[200px] flex-shrink-0 sticky left-0 z-10 bg-surface-1 border-r border-line px-3 py-2 text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
                Команда
              </div>
              <div className="relative h-8 flex-shrink-0" style={{ width: trackW }}>
                {hourTicks.map((h) => (
                  <div key={h} className="absolute top-0 bottom-0 border-l border-line/50 text-[10px] text-ink-3 tabular-nums pl-1 pt-1.5"
                    style={{ left: h * HOUR_W }}>
                    {h < 24 ? String(h).padStart(2, '0') : ''}
                  </div>
                ))}
              </div>
            </div>

            {/* строки команд */}
            {teamRows.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-ink-3">
                Нет включённых команд — отметь команды в левой панели.
              </div>
            )}
            {teamRows.map((t) => {
              const list = dayDeals.filter((d) => d.team_id === t.id);
              return (
                <div key={t.id} className="flex border-b border-line/60 min-h-[44px]">
                  <div className="w-[104px] sm:w-[200px] flex-shrink-0 sticky left-0 z-10 bg-surface-1 border-r border-line px-3 py-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="text-xs text-ink-1 truncate">{t.icon ? `${t.icon} ` : ''}{t.name}</span>
                    {list.length > 0 && <span className="text-[10px] text-ink-3 tabular-nums ml-auto">{list.length}</span>}
                  </div>
                  {renderTrack(list, t.color, t.id)}
                </div>
              );
            })}

            {/* Без команды */}
            {noTeam.length > 0 && (
              <div className="flex border-b border-line/60 min-h-[44px] bg-surface-2/30">
                <div className="w-[104px] sm:w-[200px] flex-shrink-0 sticky left-0 z-10 bg-surface-1 border-r border-line px-3 py-2 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-ink-3/40 flex-shrink-0" />
                  <span className="text-xs text-ink-3 truncate italic">Без команды</span>
                  <span className="text-[10px] text-ink-3 tabular-nums ml-auto">{noTeam.length}</span>
                </div>
                {renderTrack(noTeam, null, null)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Draggable event chip ────────────────────────────────

function DraggableEventChip({ deal, team, onClick }: { deal: Deal; team: Team | null | undefined; onClick: () => void }) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `deal:${deal.id}`,
    data: { type: 'deal', dealId: deal.id },
  });
  const shiftMs = useTzShiftMs();
  const uid = useStore((s) => s.currentUser?.employee_id) ?? null;
  const formats = useStore((s) => s.game_formats);
  const own = isOwnDeal(deal, uid, team);
  const color = team?.color ?? '#a09ac0';
  const emoji = dealEmoji(deal, formats);
  const timeRange = deal.event.start_at && deal.event.end_at
    ? `${formatHM(deal.event.start_at + shiftMs)}-${formatHM(deal.event.end_at + shiftMs)}`
    : '';
  const title = dealTitle(deal);

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      className={cn(
        'w-full text-left flex items-center gap-1 px-1.5 h-5 rounded text-[10px] font-medium hover:brightness-110 transition-all overflow-hidden cursor-grab active:cursor-grabbing text-ink-1',
        isDragging && 'opacity-30',
      )}
      style={{ backgroundColor: color + '33' }}
    >
      <span className="w-1 h-3 rounded flex-shrink-0" style={{ backgroundColor: color }} />
      {own && <OwnDot />}
      {!deal.bitrix_id && <ManualDot />}
      <span className="text-[10px] flex-shrink-0">{emoji}</span>
      <EventBadge deal={deal} compact />
      <span className="truncate flex-shrink min-w-0"><DealLabel deal={deal} /></span>
      <span className="flex items-center gap-1 ml-auto flex-shrink-0">
        <PrebookingBadge deal={deal} />
        <MarkerBadge deal={deal} className="!w-3.5 !h-3.5" />
        {timeRange && <span className="text-[9px] opacity-75 tabular-nums">{timeRange}</span>}
      </span>
    </button>
  );
}

// ── Draggable team row in sidebar (with expandable members) ─────

function DraggableTeamRow({ team, indexNum, members, expanded, onToggleExpand, checked, onToggle, onEdit }: {
  team: Team; indexNum: number; members: Employee[]; expanded: boolean; onToggleExpand: () => void;
  checked: boolean; onToggle: () => void; onEdit: () => void;
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `team:${team.id}`,
    data: { type: 'team', teamId: team.id },
  });

  return (
    <div>
      <div
        ref={setNodeRef}
        className={cn(
          'group flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all',
          'hover:bg-surface-2',
          !checked && 'opacity-50',
          isDragging && 'opacity-30 ring-2 ring-accent',
        )}
      >
        <button onClick={onToggleExpand}
          className="text-ink-3 hover:text-ink-1 flex-shrink-0 w-3.5 h-3.5 grid place-items-center"
          title={expanded ? 'Свернуть' : 'Развернуть состав'}>
          <ChevronDown className={cn('w-3 h-3 transition-transform', !expanded && '-rotate-90')} />
        </button>
        <input type="checkbox" checked={checked} onChange={onToggle}
          className="w-3.5 h-3.5 accent-accent flex-shrink-0" />
        <span
          className="w-4 h-4 rounded text-[9px] font-bold text-white grid place-items-center flex-shrink-0"
          style={{ backgroundColor: team.color }}
          title={team.description || undefined}
        >{team.icon || team.order || indexNum}</span>
        <span className="flex-1 truncate text-ink-1">{team.name}</span>
        <span className="text-[9px] text-ink-3 tabular-nums flex-shrink-0">{members.length}</span>
        <button
          {...attributes}
          {...listeners}
          className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-ink-3 hover:text-ink-1 flex-shrink-0 -mr-1"
          title="Тащи на день календаря"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button onClick={onEdit} className="opacity-0 group-hover:opacity-100 text-ink-3 hover:text-ink-1 flex-shrink-0" title="Редактировать">
          <span className="text-xs">✎</span>
        </button>
      </div>
      {expanded && (
        <div className="ml-5 pl-2 border-l border-line/60 space-y-0.5 py-0.5">
          {members.length === 0 ? (
            <div className="text-[10px] text-ink-3 italic px-2 py-1">пусто</div>
          ) : (
            members.map((m) => (
              <DraggableEmployeeRow key={m.id} employee={m} teamColor={team.color} teamId={team.id} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Draggable employee row inside an expanded team ──────

function DraggableEmployeeRow({ employee, teamColor, teamId }: {
  employee: Employee; teamColor: string; teamId: string;
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `employee:${employee.id}:${teamId}`,
    data: { type: 'employee', employeeId: employee.id, fromTeamId: teamId },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        'group flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] transition-all cursor-grab active:cursor-grabbing hover:bg-surface-2',
        isDragging && 'opacity-30 ring-1 ring-accent',
      )}
      title="Тащи на день календаря — создаст сделку с этим сотрудником"
    >
      <span className="w-1 h-3 rounded flex-shrink-0" style={{ backgroundColor: teamColor }} />
      <span className="w-4 h-4 rounded-full grid place-items-center text-[8px] font-bold text-white flex-shrink-0"
        style={{ backgroundColor: `hsl(${hashHue(employee.full_name)}, 55%, 48%)` }}>
        {initialsOf(employee.full_name)}
      </span>
      <span className="flex-1 truncate text-ink-2">{employee.full_name}</span>
      {employee.role && (
        <span className="text-[9px] text-ink-3 flex-shrink-0">{employee.role}</span>
      )}
      <GripVertical className="w-3 h-3 text-ink-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
    </div>
  );
}

/** Радио-строка маркера в боковой панели (фильтр календаря по маркеру). */
function MarkerRadio({ label, icon, checked, onChange, count }: {
  label: string; icon?: React.ReactNode; checked: boolean; onChange: () => void; count?: number;
}) {
  return (
    <label className={cn('flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer hover:bg-surface-2',
      !checked && 'text-ink-2')}>
      <input type="radio" checked={checked} onChange={onChange}
        className="w-3.5 h-3.5 accent-accent flex-shrink-0" />
      {icon && <span className="w-4 flex-shrink-0 grid place-items-center">{icon}</span>}
      <span className={cn('flex-1 truncate', checked ? 'text-ink-1 font-semibold' : 'text-ink-2')}>{label}</span>
      {count != null && (
        <span className={cn('text-[10px] tabular-nums px-1.5 rounded-full flex-shrink-0',
          count > 0 ? 'bg-accent-soft text-accent' : 'bg-surface-3 text-ink-3')}>{count}</span>
      )}
    </label>
  );
}

function TeamCheckbox({ label, color, checked, onChange }: {
  label: string; color: string; checked: boolean; onChange: () => void;
}) {
  return (
    <label className={cn('flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer hover:bg-surface-2',
      !checked && 'opacity-50')}>
      <input type="checkbox" checked={checked} onChange={onChange}
        className="w-3.5 h-3.5 accent-accent flex-shrink-0" />
      <span className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="flex-1 truncate text-ink-1 font-semibold">{label}</span>
    </label>
  );
}
